import React, { useEffect, useState } from "react";
import { Alert, Text, View } from "react-native";
import { Save, Image as ImageIcon, FileCheck } from "lucide-react-native";
import { api, API_URL } from "../api/client";
import { Button, Card, Field, ScreenHeader, Toolbar } from "../components/UI";
import { colors, styles } from "../styles";

export function SettingsScreen() {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [logoSaving, setLogoSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [stampSaving, setStampSaving] = useState(false);
  const [stampFile, setStampFile] = useState(null);
  const [stampPreview, setStampPreview] = useState("");
  const [message, setMessage] = useState(null);

  useEffect(() => {
    api.settings().then((data) => {
      const settings = {
        print_stamp_signature_on_draft_pdf: 0,
        print_stamp_signature_on_final_pdf: 1,
        ...(data || {})
      };
      setForm(settings);
      setLogoPreview(logoSrc(data?.logo_url));
      setStampPreview(logoSrc(data?.quotation_stamp_signature_image_url));
    }).catch((error) => Alert.alert("Settings", error.message));
  }, []);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setMessage(null);
    try {
      const saved = await api.saveSettings({
        ...form,
        company_name: form.company_name || "Company Name"
      });
      setForm(saved);
      setMessage({ type: "success", text: "Company settings saved successfully." });
      Alert.alert("Settings saved", "Company profile updated.");
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Settings could not be saved." });
      Alert.alert("Settings", error.message);
    } finally {
      setSaving(false);
    }
  };

  const chooseLogo = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"];
    if (!allowed.includes(file.type)) {
      setMessage({ type: "error", text: "Unsupported logo file. Use PNG, JPG, JPEG, WEBP, or SVG." });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: "error", text: "Logo file is too large. Maximum size is 2 MB." });
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setMessage(null);
  };

  const uploadLogo = async () => {
    if (!logoFile) {
      setMessage({ type: "error", text: "Please choose a logo file first." });
      return;
    }
    setLogoSaving(true);
    setMessage(null);
    try {
      const saved = await api.uploadLogo(logoFile);
      setForm(saved);
      setLogoFile(null);
      setLogoPreview(logoSrc(saved.logo_url));
      setMessage({ type: "success", text: "Company logo saved successfully." });
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Logo could not be uploaded." });
    } finally {
      setLogoSaving(false);
    }
  };

  const removeLogo = async () => {
    setLogoSaving(true);
    setMessage(null);
    try {
      const saved = await api.removeLogo();
      setForm(saved);
      setLogoFile(null);
      setLogoPreview("");
      setMessage({ type: "success", text: "Company logo removed." });
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Logo could not be removed." });
    } finally {
      setLogoSaving(false);
    }
  };

  const chooseStamp = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowed.includes(file.type)) {
      setMessage({ type: "error", text: "Unsupported stamp/signature file. Use PNG, JPG, JPEG, or WEBP." });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: "error", text: "Stamp/signature file is too large. Maximum size is 2 MB." });
      return;
    }
    setStampFile(file);
    setStampPreview(URL.createObjectURL(file));
    setMessage(null);
  };

  const uploadStamp = async () => {
    if (!stampFile) {
      setMessage({ type: "error", text: "Please choose a stamp/signature image first." });
      return;
    }
    setStampSaving(true);
    setMessage(null);
    try {
      const saved = await api.uploadStampSignature(stampFile, {
        print_stamp_signature_on_draft_pdf: Boolean(form.print_stamp_signature_on_draft_pdf),
        print_stamp_signature_on_final_pdf: Boolean(form.print_stamp_signature_on_final_pdf)
      });
      setForm(saved);
      setStampFile(null);
      setStampPreview(logoSrc(saved.quotation_stamp_signature_image_url));
      setMessage({ type: "success", text: "Quotation stamp/signature saved successfully." });
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Stamp/signature could not be uploaded." });
    } finally {
      setStampSaving(false);
    }
  };

  const saveStampOptions = async () => {
    setStampSaving(true);
    setMessage(null);
    try {
      const saved = await api.saveStampSignatureSettings({
        print_stamp_signature_on_draft_pdf: Boolean(form.print_stamp_signature_on_draft_pdf),
        print_stamp_signature_on_final_pdf: Boolean(form.print_stamp_signature_on_final_pdf)
      });
      setForm(saved);
      setStampPreview(logoSrc(saved.quotation_stamp_signature_image_url));
      setMessage({ type: "success", text: "Quotation stamp/signature options saved." });
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Stamp/signature options could not be saved." });
    } finally {
      setStampSaving(false);
    }
  };

  const removeStamp = async () => {
    setStampSaving(true);
    setMessage(null);
    try {
      const saved = await api.removeStampSignature();
      setForm(saved);
      setStampFile(null);
      setStampPreview("");
      setMessage({ type: "success", text: "Quotation stamp/signature removed." });
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Stamp/signature could not be removed." });
    } finally {
      setStampSaving(false);
    }
  };

  const toggleStampOption = (key) => {
    setForm({ ...form, [key]: form[key] ? 0 : 1 });
  };

  return (
    <View style={styles.page}>
      <ScreenHeader title="Company Profile & Branding" subtitle="These details appear automatically on exported quotation documents" />

      {message ? (
        <Text style={[styles.message, message.type === "error" ? styles.errorMessage : styles.successMessage]}>
          {message.text}
        </Text>
      ) : null}

      <View style={styles.row}>
        {/* Company Logo Section */}
        <Card style={{ flex: 1, minWidth: 320 }}>
          <Text style={styles.h2}>Company Header Logo</Text>
          <View style={styles.logoPanel}>
            {logoPreview ? (
              <img src={logoPreview} alt="Company logo preview" style={logoImageStyle} />
            ) : (
              <View style={noImagePlaceholder}>
                <ImageIcon size={32} color={colors.subtle} />
                <Text style={styles.small}>No logo uploaded yet.</Text>
              </View>
            )}
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.webp,.svg,image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={chooseLogo}
              style={fileInputStyle}
            />
            <Toolbar>
              <Button icon={Save} onPress={uploadLogo} disabled={logoSaving} loading={logoSaving}>Save Logo</Button>
              <Button variant="secondary" onPress={removeLogo} disabled={logoSaving || !form.logo_url}>Remove Logo</Button>
            </Toolbar>
          </View>
        </Card>

        {/* Stamp & Signature Section */}
        <Card style={{ flex: 1, minWidth: 320 }}>
          <Text style={styles.h2}>Quotation Stamp & Signature</Text>
          <View style={styles.logoPanel}>
            {stampPreview ? (
              <img src={stampPreview} alt="Stamp and signature preview" style={stampImageStyle} />
            ) : (
              <View style={noStampPlaceholder}>
                <FileCheck size={32} color={colors.subtle} />
                <Text style={styles.small}>No stamp image uploaded.</Text>
              </View>
            )}
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
              onChange={chooseStamp}
              style={fileInputStyle}
            />
            
            <View style={{ gap: 8 }}>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={Boolean(form.print_stamp_signature_on_draft_pdf)}
                  onChange={() => toggleStampOption("print_stamp_signature_on_draft_pdf")}
                  style={{ accentColor: colors.primary, width: 18, height: 18 }}
                />
                <Text style={styles.checkLabel}>Enable Stamp on Draft PDF</Text>
              </label>

              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={Boolean(form.print_stamp_signature_on_final_pdf)}
                  onChange={() => toggleStampOption("print_stamp_signature_on_final_pdf")}
                  style={{ accentColor: colors.primary, width: 18, height: 18 }}
                />
                <Text style={styles.checkLabel}>Enable Stamp on Final PDF</Text>
              </label>
            </View>

            <Toolbar>
              <Button icon={Save} onPress={uploadStamp} disabled={stampSaving} loading={stampSaving}>Save Stamp</Button>
              <Button variant="secondary" onPress={saveStampOptions} disabled={stampSaving}>Save Options</Button>
              <Button variant="secondary" onPress={removeStamp} disabled={stampSaving || !form.quotation_stamp_signature_image_url}>Remove</Button>
            </Toolbar>
          </View>
        </Card>
      </View>

      {/* Company Profile Details */}
      <Card>
        <Text style={styles.h2}>Company Official Profile</Text>
        <View style={styles.row}>
          <Field label="Company Name *" value={form.company_name} onChangeText={(v) => setForm({ ...form, company_name: v })} style={{ flex: 2, minWidth: 220 }} />
          <Field label="Phone Number" value={form.phone} onChangeText={(v) => setForm({ ...form, phone: v })} style={{ flex: 1, minWidth: 160 }} />
          <Field label="Official Email" value={form.email} onChangeText={(v) => setForm({ ...form, email: v })} style={{ flex: 1, minWidth: 180 }} />
          <Field label="Website URL" value={form.website} onChangeText={(v) => setForm({ ...form, website: v })} style={{ flex: 1, minWidth: 180 }} />
          <Field label="NTN / Tax Number" value={form.tax_number} onChangeText={(v) => setForm({ ...form, tax_number: v })} style={{ flex: 1, minWidth: 160 }} />
        </View>

        <View style={styles.row}>
          <Field label="Physical Address" value={form.address} onChangeText={(v) => setForm({ ...form, address: v })} multiline style={{ flex: 2, minWidth: 260 }} />
          <Field label="Footer Note" value={form.footer_note} onChangeText={(v) => setForm({ ...form, footer_note: v })} multiline style={{ flex: 2, minWidth: 260 }} />
          <Field label="Default Terms and Conditions" value={form.terms_conditions} onChangeText={(v) => setForm({ ...form, terms_conditions: v })} multiline style={{ flex: 2, minWidth: 260 }} />
        </View>

        <Toolbar style={{ marginTop: 8 }}>
          <Button icon={Save} onPress={save} disabled={saving} loading={saving}>Save Company Profile</Button>
        </Toolbar>
      </Card>
    </View>
  );
}

function logoSrc(logoUrl) {
  if (!logoUrl) return "";
  if (/^https?:\/\//i.test(logoUrl)) return logoUrl;
  return `${API_URL.replace(/\/api$/, "")}${logoUrl}`;
}

const logoImageStyle = {
  maxWidth: 240,
  maxHeight: 100,
  objectFit: "contain",
  border: `1px solid ${colors.border}`,
  borderRadius: "12px",
  padding: "12px",
  background: "#fff"
};

const stampImageStyle = {
  width: 240,
  height: 140,
  objectFit: "contain",
  border: `1px solid ${colors.border}`,
  borderRadius: "12px",
  padding: "12px",
  background: "#fff"
};

const noImagePlaceholder = {
  width: 240,
  height: 100,
  borderWidth: 1,
  borderColor: colors.border,
  borderRadius: 12,
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: colors.bg,
  gap: 6
};

const noStampPlaceholder = {
  width: 240,
  height: 140,
  borderWidth: 1,
  borderColor: colors.border,
  borderRadius: 12,
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: colors.bg,
  gap: 6
};

const fileInputStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: "12px",
  padding: "10px 14px",
  backgroundColor: "#fff",
  width: "100%",
  fontSize: "14px"
};

const checkboxLabelStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  cursor: "pointer"
};
